import React, { useEffect, useState, useRef } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { AD_LINK, AD_VIEW_DURATION_MS } from '../config/constants';

interface AdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AdModal: React.FC<AdModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showThanks, setShowThanks] = useState(false);
  const [adClicked, setAdClicked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const adWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setTimeRemaining(null);
    setShowThanks(false);
    setAdClicked(false);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen]);

  // Timer logic: starts only after ad is clicked
  useEffect(() => {
    if (!adClicked) return;

    const duration = AD_VIEW_DURATION_MS / 1000;
    setTimeRemaining(duration);

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          setShowThanks(true);
          onSuccess?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [adClicked, onSuccess]);

  const handleAdClick = () => {
    adWindowRef.current = window.open(AD_LINK, 'ad_window', 'width=800,height=600');
    if (!adWindowRef.current) {
      alert('Please allow pop-ups to view the ad. Thank you for supporting us!');
    } else {
      setAdClicked(true);
    }
  };

  const handleClose = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700 relative animate-slide-up">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          aria-label="Close"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {!showThanks ? (
          <>
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              Support the Developer
            </h2>
            
            {!adClicked ? (
              <>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Help keep MathMaster free by viewing a quick ad. Just keep this window open for 10s.
                </p>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    📢 An ad will open in a new window. <strong>The timer will start after you click the ad link!</strong>
                  </p>
                </div>

                <button
                  onClick={handleAdClick}
                  className="w-full py-3 px-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-5 h-5" />
                  Open Ad & Start Timer
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  ⏱️ Keep the ad window open until the timer reaches 0
                </p>

                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
                    {timeRemaining}s
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Keep ad window open
                  </p>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  You can close this popup anytime - just keep the ad window open until the timer finishes.
                </p>
              </>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">🙏</div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Thank You!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your support means the world to us. Enjoy the game!
            </p>
            <button
              onClick={handleClose}
              className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200"
            >
              Close & Play
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
